/**
 * Preset catalog — loaded from shared/presets/catalog.json (repo root).
 * Add demos there; do not hard-code preset lists in the app.
 */

import catalogJson from "../../../shared/presets/catalog.json";
import {
  audioParamsFromSnake,
  cloneAudioParams,
  defaultAudioParams,
  type AudioParams,
} from "../audio/params";
import {
  cloneRenderParams,
  cloneSimParams,
  renderParamsFromSnake,
  simParamsFromSnake,
  type RenderParams,
  type SimParams,
} from "../sim/params";

export interface Preset {
  id: string;
  title: string;
  subtitle: string;
  citation: string;
  story: string;
  tags: string[];
  hotkey: string | null;
  params: SimParams;
  render: RenderParams;
  /** Track A — always defined post-load (defaults off). */
  audio: AudioParams;
}

type CatalogEntry = {
  id: string;
  title: string;
  subtitle?: string;
  citation?: string;
  story?: string;
  tags?: string[];
  hotkey?: string;
  params?: Record<string, unknown>;
  render?: Record<string, unknown>;
  audio?: Record<string, unknown>;
};

function entryToPreset(e: CatalogEntry): Preset {
  const tags = e.tags ?? [];
  return {
    id: e.id,
    title: e.title,
    subtitle: e.subtitle ?? "",
    citation: e.citation ?? "",
    story: e.story ?? "",
    tags,
    hotkey: e.hotkey ?? null,
    // Heuristic defaults fill unset keys (living rechoose, voxel freeze, …).
    // Keys present in e.params always win.
    params: simParamsFromSnake(e.params, { tags }),
    render: renderParamsFromSnake(e.render),
    audio: e.audio
      ? audioParamsFromSnake(e.audio)
      : defaultAudioParams(),
  };
}

const entries = catalogJson as CatalogEntry[];

/** Ordered list (catalog file order). */
export const PRESETS: Preset[] = entries.map(entryToPreset);

const byId = new Map(PRESETS.map((p) => [p.id, p]));

/** hotkey character → preset id (from catalog). */
export const PRESET_HOTKEYS: Record<string, string> = {};
for (const p of PRESETS) {
  if (p.hotkey) PRESET_HOTKEYS[p.hotkey] = p.id;
}

export function listPresets(): Preset[] {
  return PRESETS.map((p) => ({
    ...p,
    params: cloneSimParams(p.params),
    render: cloneRenderParams(p.render),
    audio: cloneAudioParams(p.audio),
  }));
}

export function getPreset(id: string): Preset {
  const p = byId.get(id);
  if (!p) {
    throw new Error(
      `unknown preset "${id}"; known: ${PRESETS.map((x) => x.id).join(", ")}`,
    );
  }
  return {
    ...p,
    params: cloneSimParams(p.params),
    render: cloneRenderParams(p.render),
    audio: cloneAudioParams(p.audio),
  };
}

export function getDefaultPresetId(): string {
  return PRESETS[0]?.id ?? "classic_dance";
}
